export type BackoffConfig = {
  baseExponentialInterval: number;
  extendedAttempts: number;
  extendedInterval: number;
  initialAttempts: number;
  initialInterval: number;
};

/**
 * Calculates the interval for the next retry attempt based on a configurable backoff strategy.
 *
 * This function implements a three-phase backoff strategy:
 * 1. Initial phase: Returns a fixed initial interval for a specified number of attempts.
 * 2. Extended phase: Returns a fixed extended interval for additional attempts.
 * 3. Exponential phase: Implements an exponential backoff for all subsequent attempts.
 *
 * @param attempt - The current attempt number (1-based index).
 * @param config - The configuration object for the backoff strategy.
 * @param config.initialInterval - The interval (in ms) to use for the initial phase.
 * @param config.initialAttempts - The number of attempts to use the initial interval.
 * @param config.extendedInterval - The interval (in ms) to use for the extended phase.
 * @param config.extendedAttempts - The number of attempts to use the extended interval.
 * @param config.baseExponentialInterval - The base interval (in ms) for the exponential phase.
 *
 * @returns The calculated interval (in milliseconds) to wait before the next attempt.
 *
 * @example
 * const config = {
 *   initialInterval: 1000,
 *   initialAttempts: 3,
 *   extendedInterval: 2000,
 *   extendedAttempts: 2,
 *   baseExponentialInterval: 5000
 * };
 * calculateBackoffInterval(1, config); // Returns 1000
 * calculateBackoffInterval(4, config); // Returns 2000
 * calculateBackoffInterval(6, config); // Returns 5000
 * calculateBackoffInterval(7, config); // Returns 10000
 */
export const calculateBackoffInterval = (attempt: number, config: BackoffConfig): number => {
  const { baseExponentialInterval, extendedAttempts, extendedInterval, initialAttempts, initialInterval } = config;

  if (attempt <= initialAttempts) {
    return initialInterval;
  } else if (attempt <= initialAttempts + extendedAttempts) {
    return extendedInterval;
  } else {
    return baseExponentialInterval * Math.pow(2, attempt - (initialAttempts + extendedAttempts + 1));
  }
};
